import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { MessageSquarePlus, UserCheck, Undo2 } from "lucide-react";

import { eventService, type EventRegistration } from "../../services/eventService";
import { todayIso } from "../../utils/dates";
import { errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { humanise } from "../register/registerLogic";
import { FeedbackForm } from "./EventForms";
import { canGiveFeedback, canWithdraw, day, isWaiting, registrationConfig, registrationStatus } from "./eventsLogic";

/** Staff: everyone registered for any event. Families: their own registrations. */
export function RegistrationsPanel({ registrations, isLoading, isError, onRetry, canManage, onChanged, initialView }: {
  registrations?: EventRegistration[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
  initialView?: string;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [rating, setRating] = useState<EventRegistration | null>(null);
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    onChanged();
  };
  const withdraw = useMutation({ mutationFn: eventService.withdraw, onSuccess: (row) => done(`Withdrawn from ${row.event_name}.`) });
  const checkIn = useMutation({ mutationFn: eventService.checkIn, onSuccess: (row) => done(`${row.user_name} is checked in.`) });
  const undo = useMutation({ mutationFn: eventService.undoCheckIn, onSuccess: (row) => done(`${row.user_name}'s check-in was undone.`) });
  const actions = [withdraw, checkIn, undo];

  const columns: RegisterColumn<EventRegistration>[] = [
    { header: "Event date", cell: (row) => day(row.event_start_date), exportValue: (row) => day(row.event_start_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Event", cell: (row) => <span className="font-medium text-gray-900">{row.event_name}</span>, exportValue: (row) => row.event_name, className: "min-w-[12rem]" },
    ...(canManage
      ? [
          { header: "Name", cell: (row: EventRegistration) => row.user_name, exportValue: (row: EventRegistration) => row.user_name, className: "text-gray-900" },
          { header: "Who", cell: (row: EventRegistration) => humanise(row.user_type), exportValue: (row: EventRegistration) => humanise(row.user_type), className: "text-gray-700" },
        ]
      : []),
    { header: "Registered on", cell: (row) => day(row.registration_date), exportValue: (row) => day(row.registration_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Needs", cell: (row) => [row.dietary_requirements, row.special_requests].filter(Boolean).join("; ") || "—", exportValue: (row) => [row.dietary_requirements, row.special_requests].filter(Boolean).join("; "), className: "max-w-[16rem] truncate text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...registrationStatus(row)} />, exportValue: (row) => registrationStatus(row).label },
    { header: "Checked in", cell: (row) => (row.check_in_time ? new Date(row.check_in_time).toLocaleString() : ""), exportValue: (row) => (row.check_in_time ? new Date(row.check_in_time).toLocaleString() : ""), exportOnly: true },
  ];

  const registration = viewing !== null ? registrations?.find((row) => row.id === viewing) : undefined;
  const doorOpen = registration ? ["published", "completed"].includes(registration.event_status) && registration.event_start_date <= today : false;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title={canManage ? "Registrations" : "My registrations"}
        description={canManage ? "Everyone registered for an event. Check people in from an event's guest list, or here." : "Events you registered for. Open one to withdraw, or to say how it went."}
        rows={registrations}
        config={registrationConfig(today, canManage)}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          actions.forEach((mutation) => mutation.reset());
          setViewing(row.id);
        }}
        openLabel={(row) => (!canManage && canGiveFeedback(row, today) ? "Give feedback" : "Open")}
        noun={["registration", "registrations"]}
        exportName={{ file: "event-registrations", title: "Event Registrations" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "Nobody has registered for an event yet." : "You have not registered for any events. Register from the Events tab."}
      />

      {registration && (
        <DetailsModal
          title={registration.event_name}
          headline={{ label: canManage ? registration.user_name : "Your registration", value: day(registration.event_start_date), note: canManage ? humanise(registration.user_type) : undefined }}
          status={registrationStatus(registration)}
          facts={[
            ["Registered on", day(registration.registration_date)],
            ["Checked in", registration.check_in_time ? new Date(registration.check_in_time).toLocaleString() : undefined],
            ["Food needs", registration.dietary_requirements ?? undefined],
            ["Emergency contact", [registration.emergency_contact, registration.emergency_phone].filter(Boolean).join(", ") || undefined],
            ["Feedback", registration.has_feedback ? "Given" : undefined],
            ["Event", registration.event_status === "cancelled" ? "Cancelled" : humanise(registration.event_status)],
          ]}
          sections={[{ title: "Requests", body: registration.special_requests }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canWithdraw(registration, today) && registration.event_status !== "cancelled" && (
                <button type="button" disabled={withdraw.isPending} onClick={() => withdraw.mutate(registration.id)} className="btn btn-outline">Withdraw</button>
              )}
              {canManage && doorOpen && (isWaiting(registration) || registration.status === "no_show") && (
                <button type="button" disabled={checkIn.isPending} onClick={() => checkIn.mutate(registration.id)} className="btn btn-primary gap-1.5"><UserCheck className="h-4 w-4" /> Check in</button>
              )}
              {canManage && registration.status === "attended" && (
                <button type="button" disabled={undo.isPending} onClick={() => undo.mutate(registration.id)} className="btn btn-outline gap-1.5"><Undo2 className="h-4 w-4" /> Undo check-in</button>
              )}
              {!canManage && canGiveFeedback(registration, today) && (
                <button type="button" onClick={() => setRating(registration)} className="btn btn-primary gap-1.5"><MessageSquarePlus className="h-4 w-4" /> Give feedback</button>
              )}
            </>
          }
        >
          {actions.map((mutation, index) => mutation.isError && <div key={index}>{errorLine(mutation.error, "That could not be done.")}</div>)}
        </DetailsModal>
      )}

      {rating && (
        <FeedbackForm
          registration={rating}
          onClose={() => setRating(null)}
          onDone={() => {
            setRating(null);
            setViewing(null);
            done(`Thank you: your feedback on ${rating.event_name} was sent.`);
          }}
        />
      )}
    </div>
  );
}
